import moment from 'jalali-moment';
import endpoints from '../../config/endpoints';
import Message from '../../layouts/Modal/Message';
import { Sidebar } from "../../layouts/Sidebar";
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { popupSlice } from '../../services/reducers';
import { setLoading } from '../../services/reducers/homeSlice';
import { services } from '../../services/reducers/serviceSlice';
import restApi from '../../services/restApi';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Swal from "sweetalert2";
import { useAppSelector } from '../../services/store';
import { useResponsive } from '../../hooks/useResponsive';
import tools from '../../utils/tools';
import useTableQuery from "../../hooks/useTableQuery";
import DataTable, { DataTableColumn } from "../../layouts/DataTable";

const UsersList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState<any>({});
  const [tab, setTab] = useState(searchParams.get('tab') || 'all');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLocalLoading] = useState(false);
  const { isMobile } = useResponsive();
  const serviceReducer = useAppSelector(state => state.serviceReducer);
  const query = useTableQuery();

  const tabTitles = {
    all: 'همه',
    USER: 'کاربران عادی',
    WORKER: 'زیباکار ها',
    OPERATOR: 'اپراتور ها',
    SUPER_ADMIN: 'ادمین ها',
  };

  // Close sidebar when clicking outside on mobile
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const sidebar = document.querySelector('.sideBarContainer');
      const toggle = document.querySelector('.mobile-menu-toggle');

      if (isMobile && sidebarOpen &&
        sidebar && !sidebar.contains(event.target as Node) &&
        toggle && !toggle.contains(event.target as Node)) {
        setSidebarOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMobile, sidebarOpen]);

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const downloadExcel = async () => {
    dispatch(setLoading(true));

    try {
      const res = await restApi(endpoints.user.excel).post({
        type: tab
      });

      if (res.data?.link) {
        window.open(res.data.link, '_blank');
        Swal.fire({
          title: 'موفق',
          text: 'فایل اکسل با موفقیت تولید شد',
          icon: 'success',
          confirmButtonText: 'متوجه شدم'
        });
      } else {
        Swal.fire({
          title: 'خطا',
          text: 'خطا در تولید فایل اکسل',
          icon: 'error',
          confirmButtonText: 'متوجه شدم'
        });
      }
    } catch (error) {
      Swal.fire({
        title: 'خطا',
        text: 'خطا در ارتباط با سرور',
        icon: 'error',
        confirmButtonText: 'متوجه شدم'
      });
    } finally {
      dispatch(setLoading(false));
    }
  };

  const deleteItem = async (id: number) => {
    const result = await Swal.fire({
      title: 'آیا مطمئن هستید؟',
      text: 'این عمل قابل بازگشت نیست',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#c89dc7',
      confirmButtonText: 'بله، حذف کن',
      cancelButtonText: 'انصراف'
    });

    if (result.isConfirmed) {
      dispatch(setLoading(true));

      try {
        const res = await restApi(process.env.REACT_APP_BASE_URL + '/admin/user/' + id, true).delete({});

        if (res.code == 204) {
          Swal.fire({
            title: 'موفق',
            text: 'کاربر با موفقیت حذف شد',
            icon: 'success',
            confirmButtonText: 'متوجه شدم'
          });
          fetchData();
        } else {
          Swal.fire({
            title: 'ناموفق',
            text: res?.data || 'خطا در حذف کاربر',
            icon: 'error',
            confirmButtonText: 'متوجه شدم'
          });
        }
      } catch (error) {
        Swal.fire({
          title: 'خطا',
          text: 'خطا در ارتباط با سرور',
          icon: 'error',
          confirmButtonText: 'متوجه شدم'
        });
      } finally {
        dispatch(setLoading(false));
      }
    }
  };

  const changeStatus = async (id: number, status: number) => {
    const result = await Swal.fire({
      title: 'آیا مطمئن هستید؟',
      text: `آیا می‌خواهید این کاربر را ${status == 0 ? 'فعال' : 'غیرفعال'} کنید؟`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#c89dc7',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'بله',
      cancelButtonText: 'انصراف'
    });

    if (result.isConfirmed) {
      dispatch(setLoading(true));

      try {
        const res = await restApi(endpoints.user.active + id, true).post({
          status: status == 1 ? 0 : 1
        });

        if (res.code == 200) {
          Swal.fire({
            title: 'موفق',
            text: `کاربر با موفقیت ${status == 0 ? 'فعال' : 'غیرفعال'} شد`,
            icon: 'success',
            confirmButtonText: 'متوجه شدم'
          });
          fetchData();
        } else {
          Swal.fire({
            title: 'ناموفق',
            text: res?.data || 'خطا در تغییر وضعیت',
            icon: 'error',
            confirmButtonText: 'متوجه شدم'
          });
        }
      } catch (error) {
        Swal.fire({
          title: 'خطا',
          text: 'خطا در ارتباط با سرور',
          icon: 'error',
          confirmButtonText: 'متوجه شدم'
        });
      } finally {
        dispatch(setLoading(false));
      }
    }
  };

  const columns: DataTableColumn<any>[] = [
    {
      key: 'actions',
      label: 'عملیات',
      cardRole: 'actions',
      render: (user, _index, isMobileRender) => isMobileRender ? (
        <>
          <button
            className={`status-btn ${user.status == 1 ? 'active' : 'inactive'}`}
            onClick={() => changeStatus(user.id, user.status)}
            title={user.status == 1 ? 'غیرفعال کردن' : 'فعال کردن'}
            aria-label={user.status == 1 ? 'غیرفعال کردن کاربر' : 'فعال کردن کاربر'}
          >
            <i className={user.status == 1 ? 'activeUser' : 'deactiveUser'}></i>
          </button>
          <button
            className="edit-btn"
            onClick={() => navigate('/user/edit/' + user.id)}
            title="ویرایش"
            aria-label="ویرایش کاربر"
          >
            <i className="edit"></i>
          </button>
          <button
            className="delete-btn"
            onClick={() => deleteItem(user.id)}
            title="حذف"
            aria-label="حذف کاربر"
          >
            <i className="trash"></i>
          </button>
        </>
      ) : (
        <div className="svgContainer">
          <i
            className={`${user.status == 1 ? 'activeUser' : 'deactiveUser'} clickable`}
            onClick={() => changeStatus(user.id, user.status)}
            title={user.status == 1 ? 'غیرفعال کردن' : 'فعال کردن'}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && changeStatus(user.id, user.status)}
          ></i>
          <i
            className="trash clickable"
            onClick={() => deleteItem(user.id)}
            title="حذف"
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && deleteItem(user.id)}
          ></i>
          <i
            className="edit clickable"
            onClick={() => navigate('/user/edit/' + user.id)}
            title="ویرایش"
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && navigate('/user/edit/' + user.id)}
          ></i>
          <i
            className="messageIcon clickable iconSize"
            onClick={() => dispatch(popupSlice.middle(<Message user={user} />))}
            title="پیام"
            role="button"
            tabIndex={0}
          ></i>
        </div>
      ),
    },
    {
      key: 'lastEntrance',
      label: 'آخرین ورود',
      sortable: true,
      render: (user) => user.lastEntrance ? moment(user.lastEntrance).format('jYYYY/jMM/jDD') : '-',
    },
    {
      key: 'codeOrServices',
      label: tab == 'WORKER' ? 'لاین خدمت دهی' : 'آخرین کد ورود',
      render: (user) => {
        if (tab != 'WORKER') return user.tmpCode || '-';
        const userServices: any = {};
        user.services?.map((e: any) => {
          const ancs = tools.findAncestors(serviceReducer.services, e.id)?.reverse();
          if (ancs && ancs.length) {
            userServices[ancs[0]?.title] = true;
          }
        });
        return Object.keys(userServices).join(', ');
      },
    },
    {
      key: 'displayName',
      label: 'نام و نام خانوادگی',
      searchable: true,
      sortable: true,
      cardRole: 'title',
      render: (user) => (user.name || '') + ' ' + (user.lastName || ''),
    },
    {
      key: 'phoneNumber',
      label: 'شماره موبایل',
      searchable: true,
      sortable: true,
      cardRole: 'subtitle',
      render: (user) => user.phoneNumber,
    },
    {
      key: 'index',
      label: 'ردیف',
      render: (_user, index) => ((query.page - 1) * query.perPage) + index + 1,
    },
  ];

  const fetchData = async () => {
    setLocalLoading(true);
    dispatch(setLoading(true));

    const params: any = { ...query.params };
    if (tab != 'all') {
      params.role = tab;
    }

    try {
      const res = await restApi(endpoints.user.index, true).get(params);

      setData({
        ...res.data,
        rolesCount: {...res.data.rolesCount,
          all: {
            title: 'همه',
            count: Object.values(res.data.rolesCount).reduce((acc, curr) => acc + ((curr as any).count || 0), 0)
          }
        }
      });
    } catch (error) {
      console.error('Error fetching users:', error);
      Swal.fire({
        title: 'خطا',
        text: 'خطا در دریافت لیست کاربران',
        icon: 'error',
        confirmButtonText: 'متوجه شدم'
      });
    } finally {
      setLocalLoading(false);
      dispatch(setLoading(false));
    }
  };

  useEffect(() => {
    fetchData();
  }, [query.page, query.perPage, query.sortBy, query.sortDir, JSON.stringify(query.filters), tab]);

  return (
    <div className="dashboardBody">
      {/* Mobile Menu Toggle */}
      {isMobile && (
        <button
          className="mobile-menu-toggle"
          onClick={toggleSidebar}
          aria-label="منوی کناری"
          aria-expanded={sidebarOpen}
        >
          ☰
        </button>
      )}

      {/* Sidebar Backdrop */}
      {isMobile && sidebarOpen && (
        <div
          className={`sidebar-backdrop ${sidebarOpen ? 'show' : ''}`}
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <Sidebar className={`sideBarContainer ${sidebarOpen ? 'mobile-open' : ''}`} />

      <main className="dashBoardMain">
        <h1 className="dashBoardTitle">لیست کاربران</h1>

        <div className="dashTabs">
          {Object.entries(tabTitles).map(([key, value]) =>
            <span
              key={key}
              className={`ordersTag clickable ${key == tab ? 'activeTab' : ''}`}
              onClick={() => {
                setTab(key);
                setSearchParams({ ['page']: '1', ['tab']: key });
              }}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  setTab(key);
                  setSearchParams({ ['page']: '1', ['tab']: key });
                }
              }}
            >
              {value}
              {data?.rolesCount && (
                <span className={`numberTag ${key == tab ? 'activeTab' : ''}`}>
                  {data?.rolesCount[key]?.count || 0}
                </span>
              )}
            </span>
          )}
        </div>

        <div className="searchContainer">
          <span className="backSpan keepRight">
            <span
              className="dashboardHeader clickable"
              onClick={() => navigate('/user/add')}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && navigate('/user/add')}
            >
              <p>افزودن کاربر جدید</p>
              <i className="addPlus"></i>
            </span>

            <span
              className="dashboardHeader clickable"
              onClick={downloadExcel}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && downloadExcel()}
            >
              <p>خروجی اکسل</p>
              <i className="addPlus"></i>
            </span>
          </span>

          <div className='itemsPerPage'>
            <label htmlFor="itemPerPage">تعداد در صفحه</label>
            <input
              id='itemPerPage'
              type="number"
              min="1"
              max="100"
              defaultValue={query.perPage}
              onChange={(input) => {
                const value = Number(input.target.value);
                if (value && value > 0 && value <= 100) {
                  query.setPerPage(value);
                }
              }}
              onBlur={(e) => {
                if (!e.target.value || Number(e.target.value) <= 0) {
                  e.target.value = query.perPage.toString();
                }
              }}
            />
          </div>
        </div>

        <DataTable
          columns={columns}
          rows={data?.users || []}
          count={data?.count || 0}
          rowKey={(user) => user.id}
          query={query}
          loading={loading}
          emptyMessage="هیچ کاربری یافت نشد"
        />
      </main>
    </div>
  );
};

export default UsersList;
